<script lang="ts" setup>
/**
 * @name Payments.vue
 * @version 1.0.0
 * @description Componente para mostrar pagos de una institución
 * @author
 * @changelog
 * 1.0.0 - 2026/01/30 - @Jorel254 - Versión inicial
 */

import { BitacoraData } from '@shared/types/base'
import { computed, ref, watch, toRef } from 'vue'
import DynamicGrid from '@/components/utils/DynamicGrid.vue'
import { formatPrice, convertFirestoreTimestamp } from '@/components/utils/functions'
import { apiService } from '@/services/apiService'
import { useStore } from 'vuex'
import BaseModal from '@/components/BaseModal.vue'
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore'
import { getStorage, ref as storageRef, uploadBytes } from 'firebase/storage'
import { dbRegistros } from '@/firebase/config'
import { INotificationPayment } from '@shared/types'
import { formatDate } from '@/utils/dateFormatter'
const props = defineProps<{
	idInstitution: string
	bitacoraData?: BitacoraData | null
	currentInstitution: string
}>()

const bitacora = computed(() => props.bitacoraData ?? null)
const storage = getStorage()
const gridBasic = computed(
	() => `"sidebar sidebar sidebar header header header"
		"sidebar sidebar sidebar header header header"
		"footer footer footer footer footer footer"`
)
const descripcionPago = ref('')
const comprobanteArchivo = ref<File[]>([])
const montoPago = computed(() => (props.bitacoraData?.precioPlan ?? 0) + (props.bitacoraData?.iva ?? 0))
const store = useStore()
const successMessage = ref('')
const showSuccessModal = ref(false)
const base64String = ref<string>('')
const proofPath = ref<string>('')
const notificationsPayments = ref<INotificationPayment[]>([])
const idInstitution = toRef(props, 'idInstitution')

const uploadProof = async (file: File): Promise<string> => {
	const safeName = (file.name || 'comprobante').replace(/[\\/]/g, '_').replace(/\.\./g, '_').trim()
	const fileName = `${Date.now()}_${safeName}`
	const storagePath = storageRef(storage, `files/institutions/${props.idInstitution}/comprobantes/${fileName}`)
	await uploadBytes(storagePath, file)
	return storagePath.fullPath.toString()
}
const clearForm = () => {
	descripcionPago.value = ''
	comprobanteArchivo.value = []
	base64String.value = ''
}
const validarFormulario = () => {
	if (!descripcionPago.value.trim()) {
		return false
	}
	if (!comprobanteArchivo.value.length) {
		return false
	}
	return true
}
const notifyPayment = async () => {
	try {
		if (!validarFormulario()) {
			store.dispatch('notification/showError', 'Todos los campos son requeridos')
			return
		}
		proofPath.value = await uploadProof(comprobanteArchivo.value[0])
		await apiService
			.post('createNotificationPayment/crear', {
				PaymentNotification: {
					description: descripcionPago.value,
					proof: proofPath.value,
					amount: montoPago.value,
					dateToSendNotification: Date.now(),
					institutionName: props.bitacoraData?.institutionName ?? '',
					idInstitution: props.idInstitution
				}
			})
			.catch((error: any) => {
				store.dispatch('notification/showError', error as Error)
			})
			.then(() => {
				successMessage.value = 'Notificación de pago guardada correctamente'
				showSuccessModal.value = true
				clearForm()
			})
	} catch (error) {
		console.error('Error al subir el comprobante:', error)
		store.dispatch('notification/showError', error as Error)
		return
	} finally {
		globalThis.location.reload()
	}
}
const obtenerPagos = async (): Promise<void> => {
	notificationsPayments.value = []
	const pagosRef = collection(dbRegistros, 'pagos')
	const q = query(pagosRef, orderBy('dateToSendNotification', 'asc'), where('idInstitution', '==', props.idInstitution))

	const querySnapshot = await getDocs(q)

	if (querySnapshot.empty) {
		return
	}

	querySnapshot.forEach(doc => {
		const data = doc.data() as INotificationPayment
		notificationsPayments.value.push({
			...data,
			dateToSendNotification: convertFirestoreTimestamp(data.dateToSendNotification)
		})
	})
}
watch(
	idInstitution,
	async newVal => {
		notificationsPayments.value = []
		if (!newVal) return
		await obtenerPagos()
	},
	{ immediate: true }
)
</script>

<template>
	<div class="plan-features">
		<article class="plan-card plan-card--blue">
			<h2 class="plan-card__title">Información de pago</h2>
			<DynamicGrid :grid-template-areas="gridBasic" grid-template-columns="repeat(5, 1fr)" grid-template-rows="auto auto" gap="4px" :area-names="['sidebar', 'header', 'footer']">
				<template #sidebar>
					<VaCard class="feature-card card-spacing">
						<VaCardContent class="feature-card__content">
							<VaAlert color="#DEF5C9" center>
								<strong class="vigencia-alert__text">Cuenta bancaria para realizar pagos</strong>
							</VaAlert>
							<div class="plan-card__list">
								<VaAlert color="#FFFFFF" center class="vigencia-alert">
									<template #icon>
										<VaIcon name="check_circle" color="#22C55E" size="large" />
									</template>
									<VaCard outline class="credit-card">
										<VaCardContent>
											<div class="row">
												<span class="col-md-12" style="font-size: 2rem; font-weight: 900; text-transform: uppercase; color: #ffffff; margin-bottom: 5px">BANORTE</span>
												<div class="col-md-12">
													<VaIcon name="sim_card" color="#f2e96d" size="2.5rem" rotation="90" />
													<VaIcon name="contactless" color="#FFFFFF" size="2rem" />
												</div>
											</div>
											<strong class="vigencia-alert__text">
												<ul>
													<li style="font-size: 1rem; text-transform: uppercase; color: #ffffff; margin-bottom: 5px">Ingeniería, Estrategia y Comunicación en Internet SC</li>
													<li style="font-size: 1rem; text-transform: uppercase; color: #ffffff; margin-bottom: 5px; letter-spacing: 0.5rem">072180008351487604</li>
													<li style="font-size: 1rem; text-transform: uppercase; color: #ffffff; margin-bottom: 5px">CLABE / 0835148760</li>
													<li style="font-size: 1.5rem; text-transform: uppercase; color: #ffffff; margin-bottom: 5px; text-align: end; margin-right: 10px">VISA</li>
												</ul>
											</strong>
										</VaCardContent>
									</VaCard>
								</VaAlert>
								<VaDivider />
							</div>
						</VaCardContent>
					</VaCard>
				</template>
				<template #header>
					<VaCard class="feature-card card-spacing">
						<VaCardContent class="feature-card__content">
							<VaAlert color="#DEF5C9" center>
								<strong class="vigencia-alert__text">Monto a pagar</strong>
							</VaAlert>
							<div class="plan-card__list">
								<VaAlert color="#FFFFFF" center class="vigencia-alert">
									<template #icon>
										<VaIcon name="check_circle" color="#22C55E" size="large" />
									</template>
									<strong class="vigencia-alert__text" style="font-size: 1.5rem; font-weight: 700; display: flex; justify-content: center">
										Precio del plan: {{ formatPrice(bitacora?.precioPlan) }}</strong
									>
									<strong class="vigencia-alert__text" style="font-size: 0.95rem; font-weight: 700; display: flex; justify-content: center"> IVA: {{ formatPrice(bitacora?.iva) }}</strong>
									<p class="date-message">El monto a pagar mostrado es el precio del plan, y el IVA.</p>
								</VaAlert>
								<VaDivider />
							</div>
						</VaCardContent>
					</VaCard>
				</template>
				<template #footer>
					<VaCard class="feature-card card-spacing">
						<VaCardContent class="feature-card__content">
							<VaAlert color="#DEF5C9" center>
								<strong class="vigencia-alert__text">Notificar pago</strong>
							</VaAlert>
							<div class="row">
								<div class="plan-card__list col-md-6">
									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Completa los datos para notificar tu pago</strong>
										<form class="payment-form" @submit.prevent="notifyPayment">
											<div class="payment-form__row">
												<VaInput v-model="descripcionPago" type="text" label="Descripción del pago" placeholder="Ej: Pago anualidad 2026" class="payment-form__input" />
												<VaInput v-model="montoPago" type="number" label="Monto (MXN)" placeholder="0.00" :min="0" step="0.01" class="payment-form__input" readonly />
											</div>
											<div class="payment-form__upload-section">
												<div class="payment-form__upload-header">
													<VaIcon name="cloud_upload" class="payment-form__upload-icon" />
													<div>
														<span class="payment-form__upload-label">Comprobante de pago</span>
														<span class="payment-form__upload-hint">PNG, JPG o JPEG — máx. recomendado 2 MB</span>
													</div>
												</div>
												<VaFileUpload
													v-model="comprobanteArchivo"
													uploadButtonText="Seleccionar archivo"
													fileTypes=".png,.jpg,.jpeg"
													color="success"
													class="payment-form__upload"
													dropzone
													dropZoneText="Arrastra tu comprobante aquí o haz clic para seleccionar" />
											</div>
											<div class="payment-form__actions">
												<VaButton type="submit" class="descargar-acuse-consumo" color="success" size="large" icon="send">Notificar pago</VaButton>
											</div>
										</form>
									</VaAlert>
								</div>
								<div class="plan-card__list col-md-6">
									<div v-if="notificationsPayments.length > 0" class="notifications-table-wrapper">
										<table class="notifications-table">
											<thead>
												<tr>
													<th class="col-description">Descripción</th>
													<th class="col-amount">Monto</th>
													<th class="col-date">Fecha</th>
												</tr>
											</thead>
											<tbody>
												<tr v-for="notification in notificationsPayments" :key="String(notification.dateToSendNotification) + '-' + String(notification.amount)">
													<td class="cell-description">{{ notification.description }}</td>
													<td class="cell-amount">{{ formatPrice(notification.amount) }}</td>
													<td class="cell-date">{{ formatDate(notification.dateToSendNotification) }}</td>
												</tr>
											</tbody>
										</table>
									</div>

									<VaList class="notifications-list" v-else>
										<VaListItem class="notification-item">
											<div class="notification-row">
												<div>No hay notificaciones de pago realizadas</div>
											</div>
										</VaListItem>
									</VaList>
								</div>
							</div>
						</VaCardContent>
					</VaCard>
				</template>
			</DynamicGrid>
		</article>
	</div>
	<BaseModal v-model="showSuccessModal" title="" :message="successMessage" :test-mode="false" size="medium">
		<template #body>
			<div class="success-message">
				<div class="success-header">
					<div class="success-icon">
						<i class="fas fa-check-circle"></i>
					</div>
					<h3 class="success-title">Éxito</h3>
				</div>
				<p class="success-description">{{ successMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>

<style scoped>
.notifications-list {
	padding: 0.75rem 0.75rem;
	overflow-y: auto;
	border-radius: 10px;
	background: #f0fdf4;
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.1),
		0 10px 24px -4px rgba(0, 0, 0, 0.15);
}

.notifications-table-wrapper {
	max-height: 400px;
	overflow: auto;
	border-radius: 12px;
	background: #f0fdf4;
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.1),
		0 10px 24px -4px rgba(0, 0, 0, 0.15);
	border: 1px solid rgba(34, 197, 94, 0.15);
}

.notifications-table {
	width: 100%;
	border-collapse: separate;
	border-spacing: 0;
}

.notifications-table thead th {
	position: sticky;
	top: 0;
	z-index: 1;
	background: #def5c9;
	color: #14532d;
	font-weight: 800;
	font-size: 0.9rem;
	letter-spacing: 0.2px;
	padding: 0.75rem 0.9rem;
	text-align: left;
	border-bottom: 1px solid rgba(34, 197, 94, 0.25);
}

.notifications-table tbody td {
	padding: 0.75rem 0.9rem;
	border-bottom: 1px solid rgba(34, 197, 94, 0.12);
	vertical-align: middle;
	font-size: 0.95rem;
}

.notifications-table tbody tr:nth-child(even) td {
	background: rgba(255, 255, 255, 0.6);
}

.notifications-table tbody tr:hover td {
	background: rgba(34, 197, 94, 0.08);
}

.col-description {
	width: 33%;
}
.col-amount {
	width: 33%;
}
.col-date {
	width: 33%;
}

.cell-description {
	color: #0f172a;
}
.cell-amount {
	font-weight: 800;
	color: #16a34a;
	white-space: nowrap;
}
.cell-date {
	color: #64748b;
	white-space: nowrap;
}

.notification-item {
	padding: 0.5rem 0.25rem;
}

.notification-row {
	display: grid;
	grid-template-columns: 1fr auto auto;
	gap: 0.75rem;
	align-items: center;
}

.credit-card {
	border: 1px solid #00d4ff;
	border-radius: 20px;
	background: linear-gradient(90deg, rgba(2, 0, 36, 1) 80%, rgba(255, 255, 255, 1) 100%) !important;
	padding: 1rem;
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.1),
		0 10px 24px -4px rgba(0, 0, 0, 0.15);
}
.success-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #28a745;
}
.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 2px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}

.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #ffffff;
}

.success-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.success-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.payment-form {
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	margin-top: 1rem;
	width: 100%;
}

.payment-form__row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1rem;
	align-items: start;
}

.payment-form__input,
.payment-form__upload {
	width: 100%;
	min-width: 0;
}

.payment-form__upload-section {
	width: 100%;
	padding: 1.25rem;
	background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
	border: 1px dashed rgba(34, 197, 94, 0.4);
	border-radius: 12px;
	transition:
		border-color 0.2s ease,
		background 0.2s ease,
		box-shadow 0.2s ease;
}

.payment-form__upload-section:hover {
	border-color: rgba(34, 197, 94, 0.65);
	background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
	box-shadow: 0 2px 8px rgba(34, 197, 94, 0.12);
}

.payment-form__upload-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1rem;
}

.payment-form__upload-icon {
	flex-shrink: 0;
	color: #22c55e;
	font-size: 1.75rem;
}

.payment-form__upload-label {
	display: block;
	font-size: 0.9375rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.85);
	letter-spacing: 0.02em;
	line-height: 1.3;
}

.payment-form__upload-hint {
	display: block;
	font-size: 0.8125rem;
	color: rgba(0, 0, 0, 0.55);
	margin-top: 0.25rem;
	letter-spacing: 0.01em;
}

.payment-form__upload-section :deep(.va-file-upload) {
	width: 100%;
}

.payment-form__upload-section :deep(.va-file-upload__dropzone) {
	border-radius: 10px;
	border-color: rgba(34, 197, 94, 0.35);
	background: #fff;
	min-height: 100px;
	transition:
		border-color 0.2s ease,
		background 0.2s ease;
}

.payment-form__upload-section :deep(.va-file-upload__dropzone:hover),
.payment-form__upload-section :deep(.va-file-upload__dropzone.va-file-upload__dropzone--dragover) {
	border-color: rgba(34, 197, 94, 0.6);
	background: rgba(240, 253, 244, 0.8);
	transition:
		border-color 0.2s ease,
		background 0.2s ease;
}

.payment-form__actions {
	display: flex;
	justify-content: center;
	padding-top: 0.5rem;
}

@media (max-width: 768px) {
	.payment-form__row {
		grid-template-columns: 1fr;
	}
}

.descargar-acuse-consumo {
	border: none;
	border-radius: 10px;
	font-weight: 600;
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;
	box-shadow: 0 2px 8px rgba(34, 197, 94, 0.35);
	flex-shrink: 0;
}
.descargar-acuse-consumo:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 14px rgba(34, 197, 94, 0.45);
}
/* Contenedor principal */
.plan-features {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	width: 100%;
	padding: 1.5rem;
	border-radius: 16px;
	gap: 0;
}

/* Card principal del plan (header con color) */
.plan-card {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	width: 100%;
	padding: 1.75rem;
	border-radius: 16px;
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.1),
		0 10px 24px -4px rgba(0, 0, 0, 0.15);
	transition:
		transform 0.25s ease,
		box-shadow 0.25s ease;
	overflow: hidden;
	position: relative;
}

.plan-card::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 4px;
	background: rgba(255, 255, 255, 0.35);
	opacity: 0;
	transition: opacity 0.25s ease;
}

.plan-card:hover {
	transform: translateY(-2px);
	box-shadow:
		0 8px 12px -2px rgba(0, 0, 0, 0.12),
		0 16px 32px -6px rgba(0, 0, 0, 0.18);
}

.plan-card:hover::before {
	opacity: 1;
}

.plan-card--blue {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
}

.plan-card__title {
	margin: 0 0 1rem;
	font-size: 1.5rem;
	font-weight: 700;
	color: #fff;
	text-align: center;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

/* Cards internas (contenido blanco) */
.feature-card {
	margin-bottom: 1rem;
	border-radius: 12px;
	border: 1px solid rgba(0, 0, 0, 0.06);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
	transition:
		box-shadow 0.2s ease,
		border-color 0.2s ease;
	overflow: hidden;
}

.feature-card:hover {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	border-color: rgba(0, 0, 0, 0.08);
}

.feature-card__content {
	padding: 1.25rem 1.5rem;
}

.card-spacing {
	margin-bottom: 1rem;
}

/* Alerta de vigencia */
.vigencia-alert {
	margin-bottom: 1.25rem;
	border-radius: 10px;
	border: none;
	box-shadow: 0 1px 3px rgba(13, 148, 136, 0.2);
}

.vigencia-alert__text {
	font-size: 1.125rem;
	font-weight: 700;
	letter-spacing: 0.02em;
}

.date-message {
	font-size: 0.9375rem;
	margin: 0;
	color: rgba(0, 0, 0, 0.85);
	line-height: 1.5;
	font-weight: 500;
}

.plan-card__list {
	margin: 0 0 1.25rem;
	padding: 5px;
	list-style: none;
	flex-grow: 1;
}
</style>
